import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  AlertCircle, 
  Upload, 
  Camera,
  CheckCircle2,
  Loader2,
  FileText,
  Image as ImageIcon,
  X,
  Clock,
  User
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AddInfo() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [requestData, setRequestData] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  
  // Form state
  const [message, setMessage] = useState('');
  const [documentPreview, setDocumentPreview] = useState(null);
  const [documentBase64, setDocumentBase64] = useState(null);
  const [documentType, setDocumentType] = useState('');
  const [selfiePreview, setSelfiePreview] = useState(null);
  const [selfieBase64, setSelfieBase64] = useState(null);
  
  const documentInputRef = useRef(null);
  const selfieInputRef = useRef(null);
  const videoRef = useRef(null);
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState(null);

  // Fetch info request details
  useEffect(() => {
    const fetchRequestDetails = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/add-info/${token}`);
        
        if (response.data.status === 'already_submitted') {
          setAlreadySubmitted(true);
        } else {
          setRequestData(response.data);
        }
      } catch (err) {
        console.error('Failed to fetch request details:', err);
        if (err.response?.status === 404) {
          setError('This link is invalid or has already been used.');
        } else if (err.response?.status === 410) {
          setError('This link has expired. Please contact support for assistance.');
        } else {
          setError('Unable to load the request. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchRequestDetails();
    }
  }, [token]);

  // Handle document upload
  const handleDocumentUpload = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Allowed file types - HEIC/HEIF are not supported by OCR/image processing
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    const fileName = file.name.toLowerCase();
    const isHeic = fileName.endsWith('.heic') || fileName.endsWith('.heif');
    
    // Check file type
    if (isHeic || file.type === 'image/heic' || file.type === 'image/heif') {
      toast.error('HEIC/HEIF files are not supported. Please convert to JPG or PNG first.');
      event.target.value = ''; // Reset input
      return;
    }
    
    if (!allowedTypes.includes(file.type) && !file.type.startsWith('image/')) {
      toast.error('Unsupported file format. Please upload JPG, PNG, or WebP images.');
      event.target.value = ''; // Reset input
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File is too large. Maximum size is 10MB.');
      event.target.value = ''; // Reset input
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setDocumentPreview(e.target.result);
      // Remove the data URL prefix for base64
      const base64 = e.target.result.split(',')[1];
      setDocumentBase64(base64);
    };
    reader.readAsDataURL(file);
  }, []);

  // Handle selfie capture
  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setShowCamera(true);
    } catch (err) {
      console.error('Camera access error:', err);
      toast.error('Unable to access camera. Please check permissions.');
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;

    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0);
    
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
    setSelfiePreview(dataUrl);
    setSelfieBase64(dataUrl.split(',')[1]);
    
    stopCamera();
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
  };

  // Handle submission
  const handleSubmit = async () => {
    if (!message && !documentBase64 && !selfieBase64) {
      toast.error('Please provide a message or upload at least one document.');
      return;
    }

    try {
      setSubmitting(true);
      
      await axios.post(`${API_URL}/api/add-info/${token}/submit`, {
        message: message || null,
        additional_document: documentBase64 || null,
        additional_selfie: selfieBase64 || null,
        document_type: documentType || null
      });
      
      setSubmitted(true);
      toast.success('Information submitted successfully!');
    } catch (err) {
      console.error('Failed to submit:', err);
      if (err.response?.status === 400) {
        toast.error(err.response.data.detail || 'Already submitted');
      } else if (err.response?.status === 410) {
        toast.error('This link has expired');
        setError('This link has expired. Please contact support.');
      } else {
        toast.error('Failed to submit. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-muted-foreground">Loading...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-red-200">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Unable to Process</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Already submitted state
  if (alreadySubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-blue-200">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-blue-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Already Submitted</h2>
            <p className="text-muted-foreground">
              You have already submitted additional information for this request. Our team is reviewing it.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-emerald-200 bg-gradient-to-br from-emerald-50 to-slate-50">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-emerald-100 rounded-full flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Information Submitted!</h2>
            <p className="text-muted-foreground mb-4">
              Thank you for providing the additional information. Our team will review it and continue processing your verification.
            </p>
            <div className="bg-white rounded-lg p-4 border">
              <p className="text-sm text-slate-600">
                You can close this window. We'll notify you once the review is complete.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main form
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="flex items-center justify-center mb-4">
            <img 
              src="/edomatch-logo-black.png" 
              alt="Edomatch" 
              className="h-10"
            />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Additional Information Required</h1>
          <p className="text-muted-foreground mt-2">
            Please provide the requested information to continue your verification
          </p>
        </div>

        {/* Request Details Card */}
        <Card className="border-amber-200 bg-amber-50/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              What We Need
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-white rounded-lg p-4 border border-amber-200">
              <p className="text-slate-700 whitespace-pre-wrap">
                {requestData?.request_message || 'Please provide additional information.'}
              </p>
            </div>
            <div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <User className="h-4 w-4" />
                <span>Requested by: {requestData?.requested_by_name || 'Verification Team'}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                <span>
                  Expires: {requestData?.expires_at ? new Date(requestData.expires_at).toLocaleDateString() : 'N/A'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Response Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Your Response</CardTitle>
            <CardDescription>
              Add a message and/or upload documents as requested
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Message */}
            <div className="space-y-2">
              <Label htmlFor="message">Message (Optional)</Label>
              <Textarea
                id="message"
                placeholder="Provide any additional context or explanation..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
              />
            </div>

            {/* Document Upload */}
            <div className="space-y-2">
              <Label>Upload Document (Optional)</Label>
              <div className="space-y-2">
                <Input
                  placeholder="Document description (e.g., 'Utility bill', 'Bank statement')"
                  value={documentType}
                  onChange={(e) => setDocumentType(e.target.value)}
                />
                
                {documentPreview ? (
                  <div className="relative">
                    <img 
                      src={documentPreview} 
                      alt="Document preview" 
                      className="w-full h-48 object-contain border rounded-lg bg-slate-50"
                    />
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2"
                      onClick={() => {
                        setDocumentPreview(null);
                        setDocumentBase64(null);
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div
                    onClick={() => documentInputRef.current?.click()}
                    className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors"
                  >
                    <Upload className="h-10 w-10 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Click to upload document</p>
                    <p className="text-xs text-slate-400 mt-1">JPG, PNG up to 10MB</p>
                  </div>
                )}
                <input
                  ref={documentInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
                  onChange={handleDocumentUpload}
                  className="hidden"
                />
              </div>
            </div>

            {/* Selfie Capture */}
            <div className="space-y-2">
              <Label>Take a New Selfie (Optional)</Label>
              
              {selfiePreview ? (
                <div className="relative">
                  <img 
                    src={selfiePreview} 
                    alt="Selfie preview" 
                    className="w-full h-48 object-contain border rounded-lg bg-slate-50"
                  />
                  <Button
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2"
                    onClick={() => {
                      setSelfiePreview(null);
                      setSelfieBase64(null);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : showCamera ? (
                <div className="relative">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full rounded-lg"
                  />
                  <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
                    <Button onClick={capturePhoto}>
                      <Camera className="h-4 w-4 mr-2" />
                      Capture
                    </Button>
                    <Button variant="outline" onClick={stopCamera}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <Button variant="outline" onClick={startCamera} className="w-full">
                  <Camera className="h-4 w-4 mr-2" />
                  Open Camera
                </Button>
              )}
            </div>

            {/* Summary */}
            {(message || documentBase64 || selfieBase64) && (
              <div className="bg-slate-50 rounded-lg p-4 border">
                <p className="text-sm font-medium text-slate-700 mb-2">You're submitting:</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {message && (
                    <li className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Message provided
                    </li>
                  )}
                  {documentBase64 && (
                    <li className="flex items-center gap-2">
                      <ImageIcon className="h-4 w-4" />
                      {documentType || 'Additional document'}
                    </li>
                  )}
                  {selfieBase64 && (
                    <li className="flex items-center gap-2">
                      <Camera className="h-4 w-4" />
                      New selfie
                    </li>
                  )}
                </ul>
              </div>
            )}

            {/* Submit Button */}
            <Button 
              onClick={handleSubmit} 
              disabled={submitting || (!message && !documentBase64 && !selfieBase64)}
              className="w-full"
              size="lg"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Information'
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground">
          Your information is securely processed and protected.
        </p>
      </div>
    </div>
  );
}
